// frontend/src/features/inventory/pages/MaterialCreatePage.tsx
// ADR-076 Rule INV-FE-03 (بازنویسی): فرم تکصفحهای ایجاد متریال
// گروه ۱: Family (code, material_type, uom)
// گروه ۲: Revision اول (name, description, sku, stock_uom, production_uom, base_qty)
// جریان: POST /materials/ → POST /material-revisions/ → redirect به DetailPage
// Compensating: اگر Revision fail شد → POST /materials/{id}/deactivate/

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import {
  useV1InventoryMaterialsCreate,
  useV1InventoryMaterialsDeactivateCreate,
  useV1InventoryMaterialRevisionsCreate,
  useV1InventoryUomList,
} from "@/api/generated/v1/v1";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { setCustomFieldValues } from "@/features/customFields/api";
import { PageBanner } from "@/shared/components/PageBanner";

const MATERIAL_TYPE_OPTIONS = [
  { value: "raw_material", label: "مواد اولیه" },
  { value: "semi_finished", label: "نیمه‌ساخته" },
  { value: "finished_product", label: "محصول نهایی" },
  { value: "spare_part", label: "قطعه یدکی" },
];

const inputStyle = {
  width: "100%",
  padding: "0.65rem 0.9rem",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid var(--glass-border)",
  borderRadius: "8px",
  color: "var(--color-text)",
  fontSize: "0.95rem",
  boxSizing: "border-box" as const,
};

const selectStyle = {
  ...inputStyle,
};

const labelStyle = {
  display: "block",
  marginBottom: "0.4rem",
  fontSize: "0.85rem",
  color: "var(--color-text-muted)",
};

export function MaterialCreatePage() {
  const navigate = useNavigate();
  const canCreate = usePermission("inventory.manage");

  // --- Family fields ---
  const [code, setCode] = useState("");
  const [materialType, setMaterialType] = useState("raw_material");

  // --- Revision fields ---
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sku, setSku] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [stockUomId, setStockUomId] = useState("");
  const [productionUomId, setProductionUomId] = useState("");
  const [baseNumerator, setBaseNumerator] = useState("");
  const [baseDenominator, setBaseDenominator] = useState("");

  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});
  const [customFieldsValid, setCustomFieldsValid] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: uomData } = useV1InventoryUomList(undefined, { query: {} });
  const uoms = uomData?.results ?? [];

  const { mutateAsync: createFamily } = useV1InventoryMaterialsCreate();
  const { mutateAsync: createRevision } = useV1InventoryMaterialRevisionsCreate();
  const { mutateAsync: deactivateFamily } = useV1InventoryMaterialsDeactivateCreate();

  const familyIdRef = useRef<string | null>(null);

  if (!canCreate) {
    navigate("/inventory/materials");
    return null;
  }

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!code.trim()) { setError("کد متریال الزامی است"); return; }
    if (!name.trim()) { setError("نام متریال (ریویژن اول) الزامی است"); return; }
    if (!customFieldsValid) { setError("لطفاً تمامی فیلدهای سفارشی الزامی را پر کنید."); return; }

    setIsSubmitting(true);

    try {
      let familyId = familyIdRef.current;

      if (!familyId) {
        // گام ۱: ایجاد Family
        const familyPayload: any = {
          code: code.trim().toUpperCase(),
          material_type: materialType,
        };
        if (stockUomId) familyPayload.uom = stockUomId;

        const family = await createFamily({ data: familyPayload });
        familyId = family.id;
        familyIdRef.current = familyId;
      }

      // گام ۲: ایجاد Revision اول
      const revPayload: any = {
        family: familyId,
        name: name.trim(),
        description: description.trim(),
        sku: sku.trim(),
        is_active: isActive,
      };
      if (stockUomId) revPayload.stock_uom = stockUomId;
      if (productionUomId) revPayload.production_uom = productionUomId;
      if (baseNumerator) revPayload.base_qty_numerator = parseInt(baseNumerator);
      if (baseDenominator) revPayload.base_qty_denominator = baseDenominator;

      await createRevision({ data: revPayload });

      // گام ۳: ذخیره فیلدهای سفارشی (ADR-095)
      if (Object.keys(customFieldValues).length > 0) {
        await setCustomFieldValues("inventory.materialfamily", familyId, customFieldValues);
      }

      familyIdRef.current = null; // موفقیت، ریست برای استفاده بعدی
      // موفقیت → رفتن به صفحه جزئیات
      navigate(`/inventory/materials/${familyId}`);

    } catch (err: any) {
      // Compensating action: اگر Family ساخته شد ولی Revision fail شد
      // طبق ADR-076 Rule MAT-DEL-01: deactivate، نه delete
      if (familyIdRef.current) {
        try {
          await deactivateFamily({ id: familyIdRef.current, data: {} as any });
        } catch {
          // deactivate fail — به هر حال خطا را نشان بده
        }
      }

      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.code?.[0] ||
        err?.response?.data?.name?.[0] ||
        "خطا در ایجاد متریال";
      setError(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="ایجاد متریال جدید"
        subtitle="تعریف خانواده قطعه جدید به همراه اولین نسخه (ریویژن)"
        icon="📦"
        helpLink="/help/inventory/material-create"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory/materials")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← انصراف و بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "700px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        {error && (
          <div
            className="mb-5 p-3"
            style={{
              background: "rgba(255,0,0,0.1)",
              borderLeft: "4px solid red",
              borderRadius: "8px",
              color: "red",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* ===== گروه ۱: مشخصات خانواده ===== */}
          <div
            style={{
              padding: "1.25rem",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--glass-border)",
              borderRadius: "10px",
            }}
          >
            <p
              style={{
                fontSize: "0.85rem",
                fontWeight: "bold",
                color: "var(--color-text-muted)",
                marginBottom: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              مشخصات پایه
            </p>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {/* کد */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>
                  کد متریال <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="مثال: STEEL-A36"
                  dir="ltr"
                  style={inputStyle}
                />
              </div>

              {/* نوع */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={labelStyle}>نوع متریال</label>
                <select
                  value={materialType}
                  onChange={(e) => setMaterialType(e.target.value)}
                  style={selectStyle}
                >
                  {MATERIAL_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ===== گروه ۲: ریویژن اول ===== */}
          <div
            style={{
              padding: "1.25rem",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid var(--glass-border)",
              borderRadius: "10px",
            }}
          >
            <p
              style={{
                fontSize: "0.85rem",
                fontWeight: "bold",
                color: "var(--color-text-muted)",
                marginBottom: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              ریویژن اول (پیشنویس)
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

              {/* نام */}
              <div>
                <label style={labelStyle}>
                  نام <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="مثال: ورق فولادی A36"
                  style={inputStyle}
                />
              </div>

              {/* توضیحات */}
              <div>
                <label style={labelStyle}>توضیحات</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="اختیاری..."
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {/* SKU */}
              <div>
                <label style={labelStyle}>کد فنی (SKU)</label>
                <input
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder="کد فنی قطعه (اختیاری)"
                  dir="ltr"
                  style={inputStyle}
                />
              </div>

              {/* واحدهای dual UOM */}
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <label style={labelStyle}>واحد انبار (پایه) <span style={{ color: "red" }}>*</span></label>
                  <select
                    value={stockUomId}
                    onChange={(e) => setStockUomId(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">انتخاب کنید...</option>
                    {uoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.symbol || u.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <label style={labelStyle}>واحد تولید (BOM)</label>
                  <select
                    value={productionUomId}
                    onChange={(e) => setProductionUomId(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">انتخاب کنید...</option>
                    {uoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.symbol || u.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ضریب تبدیل */}
              {stockUomId && productionUomId && stockUomId !== productionUomId ? (
                <div
                  style={{
                    padding: "0.75rem 1rem",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "8px",
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>
                    ضریب تبدیل: X واحد تولید = Y واحد انبار
                  </p>
                  <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                    <input
                      type="number"
                      value={baseNumerator}
                      onChange={(e) => setBaseNumerator(e.target.value)}
                      placeholder="X"
                      min="1"
                      dir="ltr"
                      style={{ ...inputStyle, width: "120px" }}
                    />
                    <span style={{ color: "var(--color-text-muted)" }}>=</span>
                    <input
                      type="number"
                      value={baseDenominator}
                      onChange={(e) => setBaseDenominator(e.target.value)}
                      placeholder="Y"
                      min="0"
                      step="0.0001"
                      dir="ltr"
                      style={{ ...inputStyle, width: "120px" }}
                    />
                  </div>
                </div>
              ) : stockUomId && productionUomId && stockUomId === productionUomId ? (
                <div
                  style={{
                    padding: "0.75rem 1rem",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "8px",
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: 0 }}>
                    چون واحد انبار و تولید یکسان است، ضریب تبدیل به صورت خودکار ۱:۱ در نظر گرفته می‌شود.
                  </p>
                </div>
              ) : null}

              {/* is_active */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.6rem 1rem",
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  border: "1px solid var(--glass-border)",
                }}
              >
                <input
                  type="checkbox"
                  id="is_active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  style={{ width: "1rem", height: "1rem", cursor: "pointer" }}
                />
                <label htmlFor="is_active" style={{ cursor: "pointer", fontSize: "0.9rem" }}>
                  متریال فعال است
                </label>
              </div>
            </div>
          </div>

          {/* ===== گروه ۳: فیلدهای سفارشی ===== */}
          <CustomFieldsPanel
            targetContentType="inventory.materialfamily"
            targetObjectId={undefined}
            hideSaveButton={true}
            context={{ material_type: materialType }}
            onChange={(vals, isValid) => {
              setCustomFieldValuesState(vals);
              setCustomFieldsValid(isValid);
            }}
          />

          {/* Submit */}
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ width: "100%", padding: "0.9rem", fontSize: "1rem" }}
          >
            {isSubmitting ? "در حال ذخیره..." : "ثبت متریال ✓"}
          </button>
        </div>
      </div>
    </div>
  );
}